import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  OnInit,
  Signal,
  inject,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import type { Contact } from '@app/models/account.model';
import { resolveNameTitleCodeForSelect } from '@app/helpers/name-title-select.helper';
import { GetLocalizedPipe } from '@app/pipes/get-localized.pipe';
import { ContactService } from '@app/services/admin/contact.service';
import { ToastService } from '@app/services/toast.service';
import { GenericStore } from '@app/stores/generic.store';
import { ValidatedDropdownComponent } from '@app/ui-components/validated-dropdown/validated-dropdown.component';
import { ValidatedInputComponent } from '@app/ui-components/validated-input/validated-input.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, catchError, throwError } from 'rxjs';

export type ContactDetailsForm = ReturnType<DetailsTabComponent['buildForm']>;

@Component({
  selector: 'app-details-tab',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ValidatedDropdownComponent,
    ValidatedInputComponent,
    GetLocalizedPipe,
  ],
  templateUrl: './details-tab.component.html',
  styleUrl: './details-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetailsTabComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly contacts = inject(ContactService);
  private readonly toast = inject(ToastService);
  private readonly i18n = inject(TranslateService);
  public store = inject(GenericStore);

  @Input({ required: true })
  contact!: Signal<Contact | null>;

  form!: ContactDetailsForm;

  ngOnInit(): void {
    this.form = this.buildForm();

    toObservable(this.contact)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((c) => {
        if (!c) return;
        this.patchContactForm(c);
      });

    toObservable(this.store.nameTitlesEntities)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const c = this.contact();
        if (c) {
          this.patchContactForm(c);
        }
      });
  }

  private patchContactForm(c: Contact): void {
    const nameTitles = this.store.nameTitlesEntities();
    this.form.patchValue(
      {
        titleCode:
          nameTitles.length > 0
            ? (resolveNameTitleCodeForSelect(c.titleCode, nameTitles) ?? '')
            : (c.titleCode ?? ''),
        firstName: c.firstName ?? '',
        middleName: c.middleName ?? '',
        lastName: c.lastName ?? '',
        phoneNumber: (c as any).phone ?? (c as any).phoneNumber ?? '',
      },
      { emitEvent: true },
    );
  }

  buildForm() {
    return this.fb.group({
      titleCode: this.fb.nonNullable.control<string>('', []),
      firstName: this.fb.nonNullable.control<string>('', [Validators.required]),
      middleName: this.fb.nonNullable.control<string>('', []),
      lastName: this.fb.nonNullable.control<string>('', [Validators.required]),
      phoneNumber: this.fb.nonNullable.control<string>('', []),
    });
  }

  save(contactId: string): Observable<void> | null {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return null;
    }
    const v = this.form.getRawValue();
    const payload = {
      titleCode: v.titleCode?.trim() || null,
      firstName: v.firstName?.trim() || '',
      middleName: v.middleName?.trim() || null,
      lastName: v.lastName?.trim() || '',
      phoneNumber: v.phoneNumber?.trim() || null,
    };

    return this.contacts.saveContact(contactId, payload).pipe(
      catchError((err) => {
        this.toast.error(this.i18n.instant('admin.toasts.saveFailed'));
        return throwError(() => err);
      })
    ) as unknown as Observable<void>;
  }
}
